import React, { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import StatusBadge from '../StatusBadge/StatusBadge';
import './DetailsPage.css';

const DetailsPage = ({
  title,
  data,
  config,
  onBack,
  onEdit = null,
  onDelete = null,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="details-page-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading details...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="details-page-error">
        <p>No details available for this record.</p>
        <button className="btn btn-secondary" onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to List
        </button>
      </div>
    );
  }

  const renderValue = (field, item) => {
    let val;
    if (typeof field.value === 'function') {
      val = field.value(item);
    } else {
      val = item[field.value];
    }

    if (field.isBadge) {
      return <StatusBadge status={val} />;
    }

    if (Array.isArray(val)) {
      return val.map((line, idx) => (
        <Fragment key={idx}>
          {line || 'N/A'}
          {idx < val.length - 1 && <br />}
        </Fragment>
      ));
    }

    return val || 'N/A';
  };

  const rawForField = (field, item) => {
    if (typeof field.value === 'function') {
      return field.value(item);
    }
    return item[field.value];
  };

  const renderFieldRow = (field, sIdx, fIdx) => {
    const rawForLayout = rawForField(field, data);
    const multilineValue = Array.isArray(rawForLayout) || field.multiline;
    const rowClass = `details-page-field-row${field.fullWidth ? ' details-page-field-full-width' : ''}`;

    return (
      <div key={`${sIdx}-${fIdx}`} className={rowClass}>
        <span className="details-page-label">{field.label}</span>
        <span className="details-page-separator">:</span>
        <div className={`details-page-value ${multilineValue ? 'details-page-value--multiline' : ''}`}>
          {renderValue(field, data)}
        </div>
      </div>
    );
  };

  const usesTwoColumnLayout = config.some((s) => s.placement === 'left' || s.placement === 'right');

  const renderLegacyGrid = () => (
    <div className="details-page-content-grid">
      {config.map((section, sIdx) => (
        <Fragment key={sIdx}>
          {section.fields.map((field, fIdx) => renderFieldRow(field, sIdx, fIdx))}
        </Fragment>
      ))}
    </div>
  );

  const renderTwoColumnLayout = () => {
    const leftSections = config.filter((s) => s.placement === 'left');
    const rightSections = config.filter((s) => s.placement === 'right');
    const neutral = config.filter((s) => !s.placement);

    return (
      <>
        <div className="details-page-two-col">
          <div className="details-page-col">
            {leftSections.map((section, sIdx) =>
              section.fields.map((field, fIdx) => renderFieldRow(field, `L${sIdx}`, fIdx))
            )}
          </div>
          <div className="details-page-col details-page-col--right">
            {rightSections.map((section, sIdx) =>
              section.fields.map((field, fIdx) => renderFieldRow(field, `R${sIdx}`, fIdx))
            )}
          </div>
        </div>
        {neutral.length > 0 && (
          <div className="details-page-content-grid details-page-content-grid--below">
            {neutral.map((section, sIdx) => (
              <Fragment key={sIdx}>
                {section.fields.map((field, fIdx) => renderFieldRow(field, sIdx, fIdx))}
              </Fragment>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="details-page-container">
      <fieldset className="details-page-fieldset">
        <legend className="details-page-legend">{title}</legend>

        {usesTwoColumnLayout ? renderTwoColumnLayout() : renderLegacyGrid()}
      </fieldset>
    </div>
  );
};

export default DetailsPage;
